<template>
  <div class="v-input" :class="[classes, { 'v-input--has-value': hasValue, 'v-input--valid-blur': isValidOnBlur }]">
    <input
      class="v-input__field"
      required
      :type="type"
      v-focus="focus"
      :min="min"
      :max="max"
      :step="step"
      :value="value"
      :disabled="disabled"
      :pattern="pattern"
      @input="onInput"
      @blur="onBlur"
    />
    <label>{{ label }}</label>
    <div class="v-input__bar"></div>
    <div class="v-input__hint" v-if="hint">{{ hint }}</div>
  </div>
</template>

<script>
export default {
  name: 'VInputR',

  props: {
    label: String,
    value: [String, Number],
    disabled: Boolean,
    type: {
      type: String,
      default: 'text'
    },
    min: {
      type: [String, Number],
      default: null
    },
    max: {
      type: [String, Number],
      default: null
    },
    pattern: {
      type: [String],
      default: null
    },
    float: {
      type: Boolean,
      default: false
    },
    precision: {
      type: Number,
      default: 2
    },
    invalid: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: null
    },
    isVisiblePopover: Boolean,
    focus: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isValidOnBlur: false
    }
  },

  computed: {
    classes () {
      return {
        'v-input--invalid': this.invalid
      }
    },
    step () {
      return this.float ? `0.${'0'.repeat(this.precision - 1)}1` : '1'
    },
    minValue () {
      return this.min !== null ? Number(this.min) : null
    },
    maxValue () {
      return this.max !== null ? Number(this.max) : null
    },
    hasValue () {
      return this.value !== null && this.value !== ''
    }
  },

  methods: {
    onInput (e) {
      let value = e.target.value

      if (this.float) {
        value = value.replace(/^(\d*[.,]?\d{0,2}).*$/, '$1')

        if (value !== '') {
          const integerPart = parseInt(value.split(/[,.]/)[0])

          if (this.maxValue !== null && integerPart >= this.maxValue) {
            value = this.maxValue.toString()
          } else if (value.includes('.') || value.includes(',')) {
            const parts = value.split(/[,.]/)
            if (parts[1] && this.precision) {
              parts[1] = parts[1].slice(0, this.precision)
              value = parts.join(value.includes('.') ? '.' : ',')
            }

            const numValue = parseFloat(value.replace(',', '.'))
            if (!isNaN(numValue) && this.maxValue !== null && numValue >= this.maxValue) {
              value = this.maxValue.toString()
            }
          }
        }
      }

      e.target.value = value
      this.$emit('input', value)
    },

    onBlur (e) {
      const value = e.target.value

      let isValid = true

      // Pattern validation
      if (this.pattern) {
        const regex = new RegExp(this.pattern)
        isValid = regex.test(value)
      }

      // min/max validation
      const numValue = parseFloat(value.replace(',', '.'))
      if (this.min !== null && numValue < this.minValue) isValid = false
      if (this.max !== null && numValue > this.maxValue) isValid = false

      this.isValidOnBlur = isValid && value !== ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.v-input {
  position: relative;

  &__field {
    width: 100%;
    font-size: 1.5rem;
    padding: 1rem 1.5rem;
    height: 5rem;
    display: block;
    font-weight: 900;
    border: none;
    border-radius: 1rem;
    background: #EEEEEE;
    transition: background-color 0.2s ease;



    &:focus {
      outline: none;
    }

    &:focus ~ label,
    &:valid ~ label {
      top: -1.5rem;
      left: 0;
      font-size: 1.3rem;
      color: $color-gray;
    }

    &:disabled ~ label {
      color: $color-gray-light;
      top: -2rem;
      left: 0;
      transform: translateY(0);
    }

    &:focus ~ .v-input__bar:before,
    &:focus ~ .v-input__bar:after {
      width: 50%;
    }
  }

  &__bar {
    position: relative;

    &:before,
    &:after {
      content: "";
      width: 0;
      height: .2rem;
      bottom: .1rem;
      position: absolute;
      transition: 0.2s ease all;
    }

    &:before {
      left: 50%;
    }

    &:after {
      right: 50%;
    }
  }

  &__hint {
    color: $color-red;
    font-size: 1.2rem;
    font-weight: 300;
    padding-top: .3rem;
  }

  label {
    top: 50%;
    transform: translateY(-50%);
    left: 1rem;
    color: $color-black;
    font-size: 1.5rem;
    position: absolute;
    pointer-events: none;
    transition: 0.2s ease all;
  }

  &--invalid {
    label {
      color: $color-red !important;
    }

    .v-input__field {
      color: $color-red !important;
      border-bottom: .2rem solid $color-red !important;
    }

    .v-input__bar:before,
    .v-input__bar:after {
      background-color: transparent !important;
    }
  }

  &--has-value {
    .v-input__field {
      background-color: #F4F4F4;
    }
  }

  &--valid-blur {
    .v-input__field {
      background-color: #DDF2E3;
    }
  }
}
</style>
